import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  CreditCard, CheckCircle, AlertTriangle, Building2, GraduationCap, Clock,
} from "lucide-react";
import useRazorpay from "../../hooks/useRazorpay";
import { getBaseUrl } from "../../utils/getBaseUrl";
import CommonToaster from "../Common/CommonToaster";
import "./public-invoice-payment.scss";

function formatCurrency(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const STATUS_CONFIG = {
  active: { label: "Awaiting Payment", className: "pip-badge-warning" },
  paid: { label: "Paid", className: "pip-badge-success" },
  expired: { label: "Expired", className: "pip-badge-secondary" },
  cancelled: { label: "Cancelled", className: "pip-badge-secondary" },
};

// Plain axios — no auth interceptors. The token in the URL is the only credential.
const publicApi = axios.create({
  baseURL: getBaseUrl(),
  headers: { "Content-Type": "application/json" },
});

const PublicFeePaymentPage = () => {
  const { token } = useParams();
  const [link, setLink] = useState(null);
  const [institute, setInstitute] = useState(null);
  const [payable, setPayable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [paying, setPaying] = useState(false);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { openCheckout, loading: rzpLoading } = useRazorpay();

  const fetchLink = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError("");
      const { data } = await publicApi.get(`/public/fee-pay/${token}`);
      setLink(data.link);
      setInstitute(data.institute);
      setPayable(!!data.payable);
    } catch (err) {
      setLoadError(err.response?.data?.message || "Failed to load payment link.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchLink(); }, [fetchLink]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data } = await publicApi.post(`/public/fee-pay/${token}/create-order`);
      await openCheckout(
        {
          razorpayKeyId: data.razorpayKeyId,
          order: data.order,
          student: data.student,
          description: data.description || "Fee Payment",
        },
        async (response) => {
          try {
            await publicApi.post(`/public/fee-pay/${token}/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            });
            setToast({ show: true, message: "Payment successful!", type: "success" });
          } catch (err) {
            // Payment may still be confirmed via webhook/reconcile — reassure the payer.
            setToast({
              show: true,
              message: err.response?.data?.message || "Payment received — confirmation may take a moment.",
              type: "success",
            });
          }
          setPaying(false);
          fetchLink();
        },
        (err) => {
          if (err?.reason !== "Payment cancelled by user.") {
            setToast({ show: true, message: err?.reason || "Payment failed.", type: "error" });
          }
          setPaying(false);
        }
      );
    } catch (err) {
      setToast({ show: true, message: err.response?.data?.message || "Failed to start payment.", type: "error" });
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="pip-wrapper">
        <div className="pip-card pip-loading">Loading payment details…</div>
      </div>
    );
  }

  if (loadError || !link) {
    return (
      <div className="pip-wrapper">
        <div className="pip-card pip-error">
          <AlertTriangle size={28} />
          <h2>Payment link not available</h2>
          <p>{loadError || "This payment link is invalid or has expired."}</p>
        </div>
      </div>
    );
  }

  const sc = STATUS_CONFIG[link.status] || {};
  const busy = paying || rzpLoading;

  return (
    <div className="pip-wrapper">
      <div className="pip-card">
        {/* Header */}
        <div className="pip-header">
          <div>
            <div className="pip-eyebrow">Fee Payment</div>
            <h1>{institute?.name || "Institute"}</h1>
            {link.studentName && (
              <div className="pip-subtitle">For {link.studentName}</div>
            )}
          </div>
          <div className={`pip-badge ${sc.className || ""}`}>{sc.label || link.status}</div>
        </div>

        {/* Status messages */}
        {link.status === "paid" && (
          <div className="pip-alert pip-alert-success">
            <CheckCircle size={18} />
            <div>
              <strong>Payment received.</strong> This fee has been paid
              {link.paidAt ? ` on ${formatDate(link.paidAt)}` : ""}. Thank you!
            </div>
          </div>
        )}
        {link.status === "expired" && (
          <div className="pip-alert pip-alert-danger">
            <AlertTriangle size={18} />
            <div>This payment link has expired. Please contact your institute for a new link.</div>
          </div>
        )}
        {link.status === "cancelled" && (
          <div className="pip-alert pip-alert-danger">
            <AlertTriangle size={18} />
            <div>This payment link has been cancelled.</div>
          </div>
        )}

        {/* Amount + Pay CTA */}
        <div className="pip-amount-row">
          <div>
            <div className="pip-muted">Amount Due</div>
            <div className="pip-amount">{formatCurrency(link.amount)}</div>
            {link.expiresAt && link.status === "active" && (
              <div className="pip-muted">
                <Clock size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                Valid until {formatDate(link.expiresAt)}
              </div>
            )}
          </div>
          <div className="pip-actions">
            {payable && link.status === "active" && (
              <button type="button" className="pip-btn pip-btn-primary" onClick={handlePay} disabled={busy}>
                <CreditCard size={16} /> {busy ? "Processing…" : `Pay ${formatCurrency(link.amount)}`}
              </button>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="pip-grid">
          <div className="pip-block">
            <h3><GraduationCap size={14} /> Course</h3>
            <div className="pip-block-body">
              <div><strong>{link.courseName || "—"}</strong></div>
              {link.batchName && <div>Batch: {link.batchName}</div>}
            </div>
          </div>

          <div className="pip-block">
            <h3><Building2 size={14} /> Billed By</h3>
            <div className="pip-block-body">
              <div><strong>{institute?.name || "—"}</strong></div>
              {institute?.email && <div>{institute.email}</div>}
              {institute?.phone && <div>{institute.phone}</div>}
            </div>
          </div>
        </div>

        {link.description && (
          <div className="pip-block">
            <h3>Note</h3>
            <p className="pip-notes">{link.description}</p>
          </div>
        )}

        <div className="pip-footer">
          Secure payment processed by Razorpay. This payment link is private — do not share publicly.
        </div>
      </div>

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />
    </div>
  );
};

export default PublicFeePaymentPage;
